import PropTypes from 'prop-types';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import useAuth from '../hooks/useAuth';
import {
  getAuthenticatedLandingPath,
  ONBOARDING_CREATE_PATH,
} from '../utils/permissionLanding';

interface AuthGuardProps {
  children: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children } = props;
  const auth = useAuth();
  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>(null);
  const is2FAEnabled = localStorage.getItem('is2FAEnabled') === 'true';
  const isOtpVerified = localStorage.getItem('isOtpVerified') === 'true';

  if (is2FAEnabled && !isOtpVerified) {
    return <Navigate to="/login/two-factor-authentication" />;
  }

  if (!auth?.isInitialized) {
    return null;
  }

  if (!auth?.isAuthenticated) {
    if (location.pathname !== requestedLocation) {
      setRequestedLocation(location.pathname);
    }

    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location }}
      />
    );
  }

  const isOnBoarded = auth?.user?.organization?.isOnBoarded;
  const isOnboardingRoute = location.pathname === ONBOARDING_CREATE_PATH;

  if (isOnBoarded === false && !isOnboardingRoute) {
    if (requestedLocation) {
      setRequestedLocation(null);
    }
    return <Navigate to={ONBOARDING_CREATE_PATH} replace />;
  }

  if (isOnBoarded === true && isOnboardingRoute) {
    return (
      <Navigate to={getAuthenticatedLandingPath(auth?.user)} replace />
    );
  }

  localStorage.removeItem('_te');
  localStorage.removeItem('is2FAEnabled');
  localStorage.removeItem('isOtpVerified');
  localStorage.removeItem('isQrVerified');
  localStorage.removeItem('_tp');

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.

  if (
    isOnBoarded !== false &&
    requestedLocation &&
    location.pathname !== requestedLocation
  ) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
};

export default AuthGuard;
